import { useState, useRef, useEffect } from 'react';
import { adminFetch } from '../config';
import './Admin.css';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export default function AdminImageUpload({
  value = '',
  onChange,
  onFileSelect,
  uploadUrl,
  onUploadSuccess,
  previewUrlResolver = (url) => url,
  placeholder = 'Or enter URL / uploads/...',
}) {
  const [dragOver, setDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!value) setPreviewFile(null);
  }, [value]);

  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (!previewFile) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(previewFile);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewFile]);

  const previewSrc = objectUrl || (value ? previewUrlResolver(value) : '');

  const handleFiles = (files) => {
    setError('');
    const file = files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png|gif|webp)$/)) {
      setError('Please use JPG, PNG, GIF or WebP.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('File too large. Max 5MB.');
      return;
    }
    setPreviewFile(file);
    if (uploadUrl) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      adminFetch(uploadUrl, { method: 'POST', body: formData })
        .then((r) => r.json())
        .then((data) => {
          if (data.error) {
            setError(data.error);
            setPreviewFile(null);
          } else {
            const path = data.value ?? data.path ?? '';
            onUploadSuccess?.(path);
            onChange?.(path);
            setPreviewFile(null);
          }
        })
        .catch(() => {
          setError('Upload failed.');
          setPreviewFile(null);
        })
        .finally(() => setUploading(false));
    } else {
      onFileSelect?.(file);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const onBrowse = () => inputRef.current?.click();

  return (
    <div className="admin-image-upload">
      <div
        className={`admin-upload-dropzone ${dragOver ? 'admin-upload-dropzone--active' : ''} ${uploading ? 'admin-upload-dropzone--uploading' : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="admin-upload-input"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {uploading ? (
          <span className="admin-upload-text">Uploading…</span>
        ) : (
          <>
            <span className="admin-upload-text">Drag and drop an image here, or</span>
            <button type="button" className="btn btn-sm admin-upload-browse" onClick={onBrowse}>
              browse from your PC
            </button>
          </>
        )}
      </div>
      {error && <p className="admin-upload-error">{error}</p>}
      {(previewSrc || value) && (
        <div className="admin-img-preview-box" style={{ marginTop: '0.75rem' }}>
          <img
            src={previewSrc}
            alt="Preview"
            className="img-preview"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <input
        type="text"
        className="admin-upload-url-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        style={{ marginTop: '0.5rem' }}
      />
    </div>
  );
}
